import { adminMutate, adminUpload, apiRead, type ApiOptions } from '../admin/primitives.js'
import {
  erreurReseau, ficheDe, lireErreur, lireMeta, lirePageActivite, lirePageListe, lirePageMateriel,
  lireResponsables, lireSynthese,
  type Droits, type ErreurSig, type Evenement, type Fiche, type Materiel, type MetaSignalements,
  type Page, type Responsable, type Resume, type Synthese,
} from './signalements-model.js'

/**
 * SIGNALEMENTS — LE SEUL MODULE QUI PARLE AU SERVEUR (09/10/2026).
 *
 * ═══ TECHNIQUE EST LA SOURCE DE VÉRITÉ ═══
 * Chaque appel part vers l'API de GESTION (`/api/v1/technique/signalements…`),
 * qui vérifie le droit de l'utilisateur, construit l'acteur et relaie vers
 * Technique côté serveur. Le navigateur ne connaît ni l'adresse de Technique,
 * ni son secret, ni ses en-têtes d'acteur : il n'envoie que ce que le contrat
 * du BFF lui demande (`docs/technique/signalements-bff-v1.md` §4).
 *
 * Aucun écran n'appelle `fetch` lui-même : tout passe ici, et tout rend un
 * `Resultat` — jamais d'exception. Une coupure réseau devient une erreur
 * « réseau », un 503 l'état « Technique momentanément indisponible ».
 *
 * Deux règles pour toute écriture :
 *  · aucune n'est réessayée automatiquement — c'est l'utilisateur qui
 *    redemande, avec la MÊME clé d'idempotence (`Intentions`) ;
 *  · `deconnexionExplicite` : seul un 401 qui dit être la fin de la session
 *    GESTION déconnecte. Le BFF n'en renvoie pas pour un refus de Technique
 *    (502 `TECHNIQUE_AUTH_FAILED`), et cette option garantit qu'un 401
 *    inattendu ne ferait pas sortir l'utilisateur de GESTION pour autant.
 */

const BASE = '/api/v1/technique/signalements'

export type Resultat<T> = { ok: true; status: number; data: T } | { ok: false; erreur: ErreurSig }

const OPTIONS: ApiOptions = { deconnexionExplicite: true }

function avecEnTetes(enTetes: Record<string, string>): ApiOptions {
  return { ...OPTIONS, headers: enTetes }
}

/** Une référence dans un chemin : encodée, même si le motif la garantit déjà sûre. */
function ref(r: string): string { return encodeURIComponent(r) }

async function lire<T>(chemin: string, interpreter: (v: unknown) => T | null): Promise<Resultat<T>> {
  try {
    const r = await apiRead<unknown>(chemin, OPTIONS)
    const e = lireErreur(r.status, r.data)
    if (e) return { ok: false, erreur: e }
    const v = interpreter(r.data)
    if (v === null) {
      // Une réponse 2xx d'une autre forme que le contrat : on ne l'affiche pas.
      return { ok: false, erreur: { ...erreurReseau(), genre: 'autre', code: 'FORME_INATTENDUE', status: r.status, message: 'La réponse du serveur n’a pas la forme attendue : rien n’est affiché plutôt qu’une donnée douteuse.' } }
    }
    return { ok: true, status: r.status, data: v }
  } catch {
    return { ok: false, erreur: erreurReseau() }
  }
}

async function ecrire<T>(
  methode: string, chemin: string, corps: unknown, interpreter: (v: unknown) => T | null, options: ApiOptions = OPTIONS,
): Promise<Resultat<T>> {
  try {
    const r = await adminMutate<{ error?: string }>(methode, chemin, corps, options)
    const e = lireErreur(r.status, r.data)
    if (e) return { ok: false, erreur: e }
    const v = interpreter(r.data)
    if (v === null) {
      return { ok: false, erreur: { ...erreurReseau(), genre: 'autre', code: 'FORME_INATTENDUE', status: r.status, message: 'L’action a été acceptée, mais la réponse n’a pas la forme attendue. Actualisez la fiche pour voir son état réel.' } }
    }
    return { ok: true, status: r.status, data: v }
  } catch {
    return { ok: false, erreur: erreurReseau() }
  }
}

const obj = (v: unknown): Record<string, unknown> | null =>
  typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : null

/* ═══════════════════════ lectures ═══════════════════════ */

export function chargerMeta(): Promise<Resultat<{ meta: MetaSignalements; droits: Droits }>> {
  return lire(`${BASE}/meta`, lireMeta)
}

export function chargerSynthese(): Promise<Resultat<Synthese>> {
  return lire(`${BASE}/summary`, lireSynthese)
}

/** `requete` vient de `requeteListe` (filtres, état, tri, taille, curseur éventuel). */
export function chargerListe(requete: URLSearchParams): Promise<Resultat<Page<Resume>>> {
  return lire(`${BASE}?${requete.toString()}`, lirePageListe)
}

export function chargerFiche(reference: string): Promise<Resultat<Fiche>> {
  return lire(`${BASE}/${ref(reference)}`, ficheDe)
}

export function chargerActivite(reference: string, limit: number, curseur: string | null): Promise<Resultat<Page<Evenement>>> {
  const q = new URLSearchParams({ limit: String(limit) })
  if (curseur) q.set('cursor', curseur)
  return lire(`${BASE}/${ref(reference)}/activity?${q.toString()}`, lirePageActivite)
}

/** Les comptes ACTIFS de GESTION qu'on peut désigner responsables (droit `manage`). */
export function chargerResponsables(): Promise<Resultat<Responsable[]>> {
  return lire(`${BASE}/responsables`, lireResponsables)
}

/** Le Parc de Technique, par le BFF (droit `create` ou `update`). */
export function chercherMateriel(q: string, curseur: string | null, limit = 20): Promise<Resultat<Page<Materiel>>> {
  const p = new URLSearchParams({ limit: String(limit) })
  if (q) p.set('q', q)
  if (curseur) p.set('cursor', curseur)
  return lire(`${BASE}/materiel?${p.toString()}`, lirePageMateriel)
}

/** L'identité de la session (pour le filtre « Affectés à moi »), lue sur `/api/v1/me`. */
export async function chargerMoi(): Promise<{ id: string; display_name: string } | null> {
  try {
    const r = await apiRead<unknown>('/api/v1/me', OPTIONS)
    const u = obj(obj(r.data)?.['user'])
    const id = u?.['id']
    const nom = u?.['display_name']
    return r.status === 200 && typeof id === 'string' ? { id, display_name: typeof nom === 'string' ? nom : '' } : null
  } catch {
    return null
  }
}

/** L'adresse GESTION d'un fichier : le BFF relaie le binaire depuis Technique, sous le domaine de GESTION. */
export function urlFichier(reference: string, fichierId: number): string {
  return `${BASE}/${ref(reference)}/files/${fichierId}`
}

/* ═══════════════════════ écritures ═══════════════════════ */

export interface CorpsCreation {
  titre: string
  categorie: string
  description?: string
  priorite?: string
  impact?: string
  emplacement?: string
  responsable_id?: string
  materiel_ids?: string[]
}

/** Création — `Idempotency-Key` OBLIGATOIRE (une clé par intention, voir `Intentions`). */
export function creer(corps: CorpsCreation, cle: string): Promise<Resultat<Fiche>> {
  return ecrire('POST', BASE, corps, ficheDe, avecEnTetes({ 'Idempotency-Key': cle }))
}

/** PATCH avec la `version` lue : sur `VERSION_CONFLICT`, l'écran relit — jamais de nouvel essai automatique. */
export function modifier(reference: string, corps: Record<string, unknown>): Promise<Resultat<Fiche>> {
  return ecrire('PATCH', `${BASE}/${ref(reference)}`, corps, ficheDe)
}

export interface CorpsTransition {
  version: number
  statut: string
  motif?: string
  resolution_summary?: string
  duplicate_of_id?: number
  responsable_id?: string
}

export function changerStatut(reference: string, corps: CorpsTransition): Promise<Resultat<Fiche>> {
  return ecrire('POST', `${BASE}/${ref(reference)}/transitions`, corps, ficheDe)
}

export function commenter(reference: string, texteCommentaire: string, cle: string): Promise<Resultat<true>> {
  return ecrire('POST', `${BASE}/${ref(reference)}/comments`, { texte: texteCommentaire }, (v) => (obj(v)?.['comment'] ? true : null),
    avecEnTetes({ 'Idempotency-Key': cle }))
}

export function lierMateriel(reference: string, materielId: string, cle: string): Promise<Resultat<true>> {
  return ecrire('POST', `${BASE}/${ref(reference)}/links`, { materiel_id: materielId }, (v) => (obj(v)?.['link'] ? true : null),
    avecEnTetes({ 'Idempotency-Key': cle }))
}

export function retirerLien(reference: string, lienId: number): Promise<Resultat<true>> {
  return ecrire('DELETE', `${BASE}/${ref(reference)}/links/${lienId}`, undefined, () => true)
}

/**
 * Dépôt d'une pièce jointe : les OCTETS BRUTS du fichier (`application/
 * octet-stream`), son nom encodé en pourcentage dans `X-File-Name`, sa propre
 * clé d'idempotence. Le BFF construit le multipart attendu par Technique.
 */
export async function deposerFichier(reference: string, fichier: File, cle: string): Promise<Resultat<true>> {
  try {
    const r = await adminUpload<{ error?: string }>('POST', `${BASE}/${ref(reference)}/files`, fichier, avecEnTetes({
      'content-type': 'application/octet-stream',
      'X-File-Name': encodeURIComponent(fichier.name),
      'Idempotency-Key': cle,
    }))
    const e = lireErreur(r.status, r.data)
    return e ? { ok: false, erreur: e } : { ok: true, status: r.status, data: true }
  } catch {
    return { ok: false, erreur: erreurReseau() }
  }
}

export function retirerFichier(reference: string, fichierId: number): Promise<Resultat<true>> {
  return ecrire('DELETE', `${BASE}/${ref(reference)}/files/${fichierId}`, undefined, () => true)
}
